import { defineStore } from 'pinia'
import { toast } from 'vue-sonner'
import * as Sentry from '@sentry/nuxt'
import { useBoards } from '~/composables/useBoards'
import type { Board, BoardCard, BoardCardDetails, BoardComment, UpdateBoardCardArgs } from '~/types/boards'

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

/**
 * The Boards service isn't there (not deployed, or this client is newer than the server): the Rust side marks
 * gRPC `Unimplemented` as `[unavailable]` (commands/boards.rs), browser mode gets a REST 404/501.
 */
export function isBoardsUnavailableError(error: unknown): boolean {
  const message = errorText(error)
  return message.startsWith('[unavailable]') || /\((404|501)\)$/.test(message)
}

/** Prefix of the server's write refusal for a member without their own linked tracker account. */
const ACCOUNT_REQUIRED_TEXT = 'Привяжите свой аккаунт'

export function isBoardAccountRequiredError(error: unknown): boolean {
  return errorText(error).includes(ACCOUNT_REQUIRED_TEXT)
}

function detailsKey(channelId: string, cardId: string): string {
  return `${channelId}:${cardId}`
}

/**
 * Board channels: the board (connection + columns + fields) per channel, its cards, and a cache of opened card
 * details. Nothing here outlives the session — the tracker is the source of truth, `BoardChanged` (see
 * useGroupsSignalR.ts) tells us which cards to refetch.
 */
export const useBoardsStore = defineStore('boards', {
  state: () => ({
    boards: {} as Record<string, Board>,
    boardLoading: {} as Record<string, boolean>,
    boardError: {} as Record<string, string | null>,
    /** get_board came back «not implemented» — the board screen shows «Недоступно в вашей версии». */
    boardUnavailable: {} as Record<string, boolean>,
    /** Cards in board order; a column is `cards.filter(c => c.column_id === column.id)`. */
    cardsByChannel: {} as Record<string, BoardCard[]>,
    cardsLoading: {} as Record<string, boolean>,
    cardsError: {} as Record<string, string | null>,
    /** Search text and sprint the cards were last loaded with ('' = the connection's sprint). */
    queryByChannel: {} as Record<string, string>,
    sprintByChannel: {} as Record<string, string>,
    /** `${channelId}:${cardId}` -> details of a card opened in the side panel. */
    cardDetails: {} as Record<string, BoardCardDetails>,
    /**
     * Channel whose write was refused because the caller has no linked tracker account — BoardView of that
     * channel opens the link dialog and clears it.
     */
    linkAccountRequest: null as string | null,
  }),

  getters: {
    cardsInColumn: (state) => (channelId: string, columnId: string): BoardCard[] =>
      (state.cardsByChannel[channelId] ?? []).filter((card) => card.column_id === columnId),
    findCard: (state) => (channelId: string, cardId: string): BoardCard | undefined =>
      state.cardsByChannel[channelId]?.find((card) => card.id === cardId),
    getCardDetails: (state) => (channelId: string, cardId: string): BoardCardDetails | undefined =>
      state.cardDetails[detailsKey(channelId, cardId)],
  },

  actions: {
    /**
     * A failed write: «no linked account» asks the board to open the link dialog (with a short toast saying why),
     * anything else is the usual error toast.
     */
    reportWriteError(channelId: string, title: string, error: unknown) {
      if (isBoardAccountRequiredError(error)) {
        this.linkAccountRequest = channelId
        toast.error(title, { description: 'Чтобы менять задачи, привяжите свой аккаунт трекера.' })
        return
      }
      toast.error(title, { description: errorText(error) })
    },

    /** Board of a channel. Cached unless `force`; a not-connected board comes back with `connected = false`. */
    async loadBoard(channelId: string, force = false): Promise<Board | null> {
      if (!force && this.boards[channelId]) return this.boards[channelId]!
      this.boardLoading[channelId] = true
      this.boardError[channelId] = null
      this.boardUnavailable[channelId] = false
      try {
        const board = await useBoards().getBoard(channelId)
        this.boards[channelId] = board
        return board
      } catch (error) {
        console.error('[BoardsStore] get_board failed:', error)
        this.boardUnavailable[channelId] = isBoardsUnavailableError(error)
        this.boardError[channelId] = errorText(error)
        return null
      } finally {
        this.boardLoading[channelId] = false
      }
    },

    /** After connect / mapping change: the server's answer is the board as it now is. */
    setBoard(board: Board) {
      this.boards[board.channel_id] = board
    },

    async loadCards(channelId: string, options: { query?: string, sprintId?: string } = {}) {
      const query = options.query ?? this.queryByChannel[channelId] ?? ''
      const sprintId = options.sprintId ?? this.sprintByChannel[channelId] ?? ''
      this.queryByChannel[channelId] = query
      this.sprintByChannel[channelId] = sprintId
      this.cardsLoading[channelId] = true
      this.cardsError[channelId] = null
      try {
        const cards = await useBoards().listBoardCards(channelId, { query, sprintId })
        // A newer search/sprint request may have been issued meanwhile — only the latest one lands.
        if (this.queryByChannel[channelId] !== query || this.sprintByChannel[channelId] !== sprintId) return
        this.cardsByChannel[channelId] = cards
      } catch (error) {
        console.error('[BoardsStore] list_board_cards failed:', error)
        this.cardsError[channelId] = errorText(error)
      } finally {
        this.cardsLoading[channelId] = false
      }
    },

    /** Realtime `BoardChanged {ChannelId, CardIds}`: refetch only those cards, and only if this board is loaded. */
    async applyBoardChanged(channelId: string, cardIds: string[]) {
      const cards = this.cardsByChannel[channelId]
      if (!cards) return
      if (!cardIds.length) {
        await this.loadCards(channelId)
        return
      }
      try {
        const fresh = await useBoards().listBoardCards(channelId, {
          query: this.queryByChannel[channelId] ?? '',
          sprintId: this.sprintByChannel[channelId] ?? '',
          cardIds,
        })
        this.mergeCards(channelId, cardIds, fresh)
      } catch (error) {
        console.error('[BoardsStore] BoardChanged refetch failed:', error)
        return
      }
      // A changed card that's open in the side panel gets its details (comments, activity) refreshed too.
      for (const cardId of cardIds) {
        if (this.cardDetails[detailsKey(channelId, cardId)]) void this.loadCardDetails(channelId, cardId)
      }
    },

    /**
     * Replaces `requestedIds` with what the server returned: updated cards in place, new ones appended, and
     * requested ids the server no longer returns (left the sprint, filtered out, deleted) removed.
     */
    mergeCards(channelId: string, requestedIds: string[], fresh: BoardCard[]) {
      const freshById = new Map(fresh.map((card) => [card.id, card]))
      const requested = new Set(requestedIds)
      const current = this.cardsByChannel[channelId] ?? []
      const next: BoardCard[] = []
      for (const card of current) {
        const updated = freshById.get(card.id)
        if (updated) {
          next.push(updated)
          freshById.delete(card.id)
        } else if (!requested.has(card.id)) {
          next.push(card)
        }
      }
      next.push(...freshById.values())
      this.cardsByChannel[channelId] = next
    },

    upsertCard(channelId: string, card: BoardCard) {
      const cards = this.cardsByChannel[channelId]
      if (!cards) return
      const index = cards.findIndex((c) => c.id === card.id)
      if (index >= 0) cards.splice(index, 1, card)
      else cards.push(card)
      const details = this.cardDetails[detailsKey(channelId, card.id)]
      if (details) details.card = card
    },

    /**
     * Optimistic move: the card jumps to its new place at once, and goes back where it was (with a toast) if the
     * tracker refuses. `beforeCardId`/`afterCardId` are its new neighbours in the target column.
     */
    async moveCard(channelId: string, cardId: string, columnId: string, neighbours: { beforeCardId?: string, afterCardId?: string } = {}) {
      const cards = this.cardsByChannel[channelId]
      if (!cards) return
      const index = cards.findIndex((c) => c.id === cardId)
      if (index < 0) return
      const snapshot = cards.slice()
      const card = { ...cards[index]!, column_id: columnId }
      const next = cards.slice()
      next.splice(index, 1)

      let insertAt = -1
      if (neighbours.beforeCardId) insertAt = next.findIndex((c) => c.id === neighbours.beforeCardId)
      if (insertAt < 0 && neighbours.afterCardId) {
        const afterIndex = next.findIndex((c) => c.id === neighbours.afterCardId)
        if (afterIndex >= 0) insertAt = afterIndex + 1
      }
      if (insertAt < 0) {
        // No neighbours given: the end of the target column.
        let lastInColumn = -1
        next.forEach((c, i) => { if (c.column_id === columnId) lastInColumn = i })
        insertAt = lastInColumn >= 0 ? lastInColumn + 1 : next.length
      }
      next.splice(insertAt, 0, card)
      this.cardsByChannel[channelId] = next

      try {
        const updated = await useBoards().moveBoardCard({
          channel_id: channelId,
          card_id: cardId,
          column_id: columnId,
          before_card_id: neighbours.beforeCardId ?? '',
          after_card_id: neighbours.afterCardId ?? '',
        })
        this.upsertCard(channelId, updated)
      } catch (error) {
        console.error('[BoardsStore] move_board_card failed:', error)
        this.cardsByChannel[channelId] = snapshot
        this.reportWriteError(channelId, 'Не удалось переместить задачу', error)
        Sentry.captureException(error, { extra: { action: 'move_board_card', channelId, cardId } })
      }
    },

    async createCard(channelId: string, title: string, columnId = '', fields: Record<string, string> = {}): Promise<BoardCard | null> {
      try {
        const card = await useBoards().createBoardCard(channelId, title, columnId, fields)
        if (!this.cardsByChannel[channelId]) this.cardsByChannel[channelId] = []
        this.upsertCard(channelId, card)
        return card
      } catch (error) {
        console.error('[BoardsStore] create_board_card failed:', error)
        this.reportWriteError(channelId, 'Не удалось создать задачу', error)
        return null
      }
    },

    async updateCard(args: UpdateBoardCardArgs): Promise<BoardCard | null> {
      try {
        const card = await useBoards().updateBoardCard(args)
        this.upsertCard(args.channel_id, card)
        const details = this.cardDetails[detailsKey(args.channel_id, args.card_id)]
        if (details && args.description !== undefined) details.description = args.description
        return card
      } catch (error) {
        console.error('[BoardsStore] update_board_card failed:', error)
        this.reportWriteError(args.channel_id, 'Не удалось сохранить задачу', error)
        return null
      }
    },

    async setAssignees(channelId: string, cardId: string, externalUserIds: string[], ioptaIds: string[]): Promise<boolean> {
      try {
        const card = await useBoards().setBoardCardAssignees(channelId, cardId, externalUserIds, ioptaIds)
        this.upsertCard(channelId, card)
        return true
      } catch (error) {
        console.error('[BoardsStore] set_board_card_assignees failed:', error)
        this.reportWriteError(channelId, 'Не удалось назначить исполнителей', error)
        return false
      }
    },

    async loadCardDetails(channelId: string, cardId: string): Promise<BoardCardDetails | null> {
      try {
        const details = await useBoards().getBoardCard(channelId, cardId)
        this.cardDetails[detailsKey(channelId, cardId)] = details
        if (details.card) this.upsertCard(channelId, details.card)
        return details
      } catch (error) {
        console.error('[BoardsStore] get_board_card failed:', error)
        return null
      }
    },

    async comment(channelId: string, cardId: string, text: string): Promise<BoardComment | null> {
      try {
        const comment = await useBoards().commentBoardCard(channelId, cardId, text)
        this.cardDetails[detailsKey(channelId, cardId)]?.comments.push(comment)
        return comment
      } catch (error) {
        console.error('[BoardsStore] comment_board_card failed:', error)
        this.reportWriteError(channelId, 'Не удалось отправить комментарий', error)
        return null
      }
    },

    async disconnect(channelId: string): Promise<boolean> {
      try {
        await useBoards().disconnectBoard(channelId)
        delete this.cardsByChannel[channelId]
        await this.loadBoard(channelId, true)
        return true
      } catch (error) {
        console.error('[BoardsStore] disconnect_board failed:', error)
        toast.error('Не удалось отключить доску', { description: errorText(error) })
        return false
      }
    },
  },
})
